import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { formatLocationDisplay } from '@/lib/google-maps';
import {
  Button,
  DateFormWidget,
  FileUpload,
  FormRow,
  LocationMapField,
} from '../controls';

const defaultDateObject = {
  startTime: null,
  endTime: null,
  startTimeMS: null,
  endTimeMS: null,
};

function buildDefaultValues(stage) {
  return {
    stageName: stage ? stage.stageName || '' : '',
    stageDescription: stage ? stage.stageDescription || '' : '',
  };
}

export function StageForm({
  stage,
  onStageSave,
  onDateChange,
  onLocationChange,
  onLocationNameChange,
  onFileUpload,
  onFileDelete,
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: buildDefaultValues(stage),
    mode: 'onSubmit',
  });

  const stageId = stage?.stageId;

  useEffect(() => {
    reset(buildDefaultValues(stage));
  }, [stageId, reset]);

  const locationName = useMemo(() => {
    if (!stage) {
      return '';
    }
    return (
      stage.location?.locationName ??
      stage.locationName ??
      formatLocationDisplay(stage.location) ??
      ''
    );
  }, [stage]);

  const dates = stage
    ? stage.times || [defaultDateObject]
    : [defaultDateObject];

  const fileList = [];

  if (stage?.photoURL) {
    fileList.push({
      uid: '-1',
      name: stage.photo,
      url: stage.photoURL,
      status: 'done',
    });
  }

  return (
    <div className="py-4">
      <form onSubmit={handleSubmit(onStageSave)}>
        <div className="flex items-start">
          <div className="w-3/4 p-4">
            <FormRow label="Stage Name" error={errors.stageName?.message}>
              <input
                type="text"
                placeholder="Stage Name"
                {...register('stageName', {
                  required: 'Stage name is required',
                })}
              />
            </FormRow>
            <DateFormWidget
              label="Dates & Times"
              dates={dates}
              handleDateChange={onDateChange}
              allowMultiple={true}
            />
          </div>
          <div className="w-1/4 p-4">
            <FormRow>
              <div className="w-full">
                <FileUpload
                  fileList={fileList}
                  onFileUpload={onFileUpload}
                  onFileDelete={onFileDelete}
                />
              </div>
            </FormRow>
          </div>
        </div>
        <FormRow>
          <LocationMapField
            key={stageId}
            shouldPromptForMap
            location={stage.location}
            locationName={locationName}
            onLocationChange={onLocationChange}
            onLocationNameChange={onLocationNameChange}
          />
        </FormRow>
        <FormRow label="General Information">
          <textarea
            placeholder="Stage Information..."
            {...register('stageDescription')}
          />
        </FormRow>
        <Button
          style={{ float: 'right', marginTop: '1rem' }}
          mode="dark"
          type="submit"
        >
          Save Stage
        </Button>
      </form>
    </div>
  );
}
